import { useState, type FormEvent } from 'react'
import { BasculeMethode, ConnexionWhatsapp, memoriserMethode, methodeMemorisee, type MethodeConnexion } from '../api/commun'
import { supabase } from '../api/supabase'
import { BoutonGoogle, connexionGoogle } from '../api/google'
import { Logo } from './Icone'
import { messageCompteCree, premiereErreur, validerInscription, type ChampInscription } from '../api/compte'
import { MESSAGE_LIEN_ENVOYE, RETOUR_CONSOLE, demanderReinitialisation } from '../api/motDePasseOublie'
import { BasculeAcces, idOngletAcces, idPanneauAcces, useContenuAcces, type ModeAcces } from './BasculeAcces'

/* Lien-texte de la carte : réutilise `.lien` de la feuille de style. */
const STYLE_LIEN_CENTRE = { display: 'block', margin: '14px auto 0', fontSize: 12.5 } as const

const NOTE_COMPTE =
  'Créer un compte ne donne pas accès à la console : un administrateur SwiftDeliver doit vous donner accès.'

/**
 * Écran de connexion : e-mail + mot de passe, ou « Continuer avec Google ».
 * Les deux voies passent ensuite par le même contrôle `is_platform_admin()`
 * (dans `App`). Les champs partent vides.
 */
export function Connexion({
  surConnexion,
  surConnexionWhatsapp,
  surInscription,
  surChangementMode,
  erreur,
}: {
  surConnexion: (email: string, motDePasse: string) => Promise<void>
  /** Appelée une fois le code WhatsApp vérifié : même contrôle d'accès que le mot de passe. */
  surConnexionWhatsapp: (userId: string) => Promise<void>
  /** Crée le compte ; `sessionOuverte` faux = confirmation par e-mail requise. */
  surInscription: (nom: string, email: string, motDePasse: string) => Promise<{ sessionOuverte: boolean }>
  /** Efface l'erreur du parent (accès refusé…) quand on change d'onglet. */
  surChangementMode: () => void
  erreur: string | null
}) {
  const [email, setEmail] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [enCours, setEnCours] = useState<'email' | 'inscription' | 'oubli' | null>(null)
  const [nom, setNom] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [mode, setMode] = useState<ModeAcces>('connexion')
  const { contenu, sortant } = useContenuAcces(mode)
  /* Numéro + code WhatsApp ou e-mail : le dernier choix est mémorisé (WhatsApp par défaut). */
  const [methode, setMethode] = useState<MethodeConnexion>(() => methodeMemorisee())
  /* Change après un refus d'accès pour remettre le formulaire WhatsApp à l'étape « numéro ». */
  const [cleWhatsapp, setCleWhatsapp] = useState(0)
  /* E-mail du compte tout juste créé (panneau de succès). */
  const [compteCree, setCompteCree] = useState<string | null>(null)
  const [champInvalide, setChampInvalide] = useState<ChampInscription | null>(null)
  /* Erreur propre à l'inscription (validation, Supabase) ; `erreur` vient du parent. */
  const [erreurLocale, setErreurLocale] = useState<string | null>(null)
  const message = erreurLocale ?? erreur
  /* « Mot de passe oublié ? » : même carte, autre formulaire. */
  const [oubli, setOubli] = useState(false)
  const [lienEnvoye, setLienEnvoye] = useState(false)
  const invalide = (c: ChampInscription) => (champInvalide === c ? true : undefined)

  function changerMode(m: ModeAcces) {
    if (m === mode) return
    setMode(m)
    setErreurLocale(null)
    setChampInvalide(null)
    setCompteCree(null)
    surChangementMode()
  }

  function changerMethode(m: MethodeConnexion) {
    if (m === methode) return
    setMethode(m)
    memoriserMethode(m)
    setErreurLocale(null)
    surChangementMode()
  }

  function ouvrirOubli() {
    setOubli(true)
    setLienEnvoye(false)
    setErreurLocale(null)
    surChangementMode()
  }

  function fermerOubli() {
    setOubli(false)
    setLienEnvoye(false)
    setErreurLocale(null)
  }

  async function demander(e: FormEvent) {
    e.preventDefault()
    if (enCours) return
    setEnCours('oubli')
    setErreurLocale(null)
    try {
      await demanderReinitialisation(email, RETOUR_CONSOLE)
      setLienEnvoye(true)
    } catch (err) {
      setErreurLocale(err instanceof Error ? err.message : 'Envoi impossible.')
    } finally {
      setEnCours(null)
    }
  }

  async function creer(e: FormEvent) {
    e.preventDefault()
    if (enCours) return
    const premiere = premiereErreur(validerInscription({ nom, email, motDePasse, confirmation }))
    if (premiere) {
      setErreurLocale(premiere.message)
      setChampInvalide(premiere.champ)
      document.getElementById(`ins-${premiere.champ}`)?.focus()
      return
    }
    setEnCours('inscription')
    setErreurLocale(null)
    setChampInvalide(null)
    surChangementMode()
    try {
      const r = await surInscription(nom.trim(), email.trim(), motDePasse)
      if (!r.sessionOuverte) {
        setCompteCree(email.trim())
        setMotDePasse('')
        setConfirmation('')
      }
    } catch (err) {
      setErreurLocale(err instanceof Error ? err.message : 'Création du compte impossible.')
    } finally {
      setEnCours(null)
    }
  }

  async function soumettre(e: FormEvent) {
    e.preventDefault()
    if (enCours) return
    setEnCours('email')
    try {
      await surConnexion(email.trim(), motDePasse)
    } finally {
      setEnCours(null)
    }
  }

  return (
    <main className="connexion">
      <div className="carte connexion-carte">
        <div className="connexion-logo"><Logo taille={34} /></div>
        <h1 className="connexion-titre">Swift<span>Deliver</span></h1>
        <p className="connexion-sous">{oubli ? 'Mot de passe oublié' : 'Supervision de la plateforme'}</p>
        <span className="pastille-reserve">Accès réservé</span>

        {oubli ? (
          <form role="group" aria-label="Mot de passe oublié" onSubmit={demander}>
            {lienEnvoye ? (
              <p className="compte-cree" role="status">{MESSAGE_LIEN_ENVOYE}</p>
            ) : (
              <>
                <p className="note-acces">
                  Saisissez l’adresse e-mail de votre compte : nous vous envoyons un lien pour choisir un nouveau mot de passe.
                </p>
                <div className="champ">
                  <label htmlFor="o-mail">Adresse e-mail</label>
                  <input
                    id="o-mail" type="email" autoComplete="email" placeholder="vous@swiftapp.ml"
                    value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
                  />
                </div>
              </>
            )}
            <p className="erreur" role="alert" aria-live="assertive">{erreurLocale}</p>
            {!lienEnvoye && (
              <button className="btn btn-plein" type="submit" disabled={enCours !== null}>
                {enCours === 'oubli' ? 'Envoi…' : 'Envoyer le lien'}
              </button>
            )}
            <button type="button" className="lien" style={STYLE_LIEN_CENTRE} onClick={fermerOubli}>
              Retour à la connexion
            </button>
          </form>
        ) : (
        <>
        <BasculeAcces mode={mode} surChangement={changerMode} desactive={enCours !== null} />
        <div
          className={`bascule-contenu${sortant ? ' sortant' : ''}`}
          key={contenu}
          role="tabpanel"
          id={idPanneauAcces}
          aria-labelledby={idOngletAcces(contenu)}
        >
          {contenu === 'connexion' && (
            <div style={{ margin: '0 0 16px' }}>
              <BasculeMethode methode={methode} surChangement={changerMethode} desactive={enCours !== null} />
            </div>
          )}
          {contenu === 'connexion' && methode === 'whatsapp' ? (
            <>
              <ConnexionWhatsapp
                key={cleWhatsapp}
                client={supabase}
                surConnecte={async (userId) => {
                  await surConnexionWhatsapp(userId)
                  setCleWhatsapp((k) => k + 1)
                }}
              />
              <p className="erreur" role="alert" aria-live="assertive">{message}</p>
              <p className="note-acces">Accès réservé aux administrateurs de la plateforme : utilisez le numéro enregistré sur votre compte.</p>
            </>
          ) : (
          <form onSubmit={contenu === 'connexion' ? soumettre : creer} noValidate={contenu === 'inscription'}>
          {contenu === 'connexion' && (
            <>
              <div className="champ">
                <label htmlFor="l-mail">Adresse e-mail</label>
                <input
                  id="l-mail" type="email" autoComplete="username" placeholder="vous@swiftapp.ml"
                  value={email} onChange={(e) => setEmail(e.target.value)} required
                />
              </div>
              <div className="champ">
                <label htmlFor="l-pass">Mot de passe</label>
                <input
                  id="l-pass" type="password" autoComplete="current-password" placeholder="Votre mot de passe"
                  value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} required
                />
              </div>

              <button type="button" className="lien" style={{ display: 'block', margin: '0 0 12px auto', fontSize: 12.5 }} onClick={ouvrirOubli} disabled={enCours !== null}>
                Mot de passe oublié ?
              </button>

              <p className="erreur" role="alert" aria-live="assertive">{message}</p>

              <button className="btn btn-plein" type="submit" disabled={enCours !== null}>
                {enCours === 'email' ? 'Connexion…' : 'Se connecter'}
              </button>

              <BoutonGoogle connexion={connexionGoogle} surErreur={(m) => setErreurLocale(m || null)} desactive={enCours !== null} />
            </>
          )}

          {contenu === 'inscription' && compteCree === null && (
            <>
              <div className="champ">
                <label htmlFor="ins-nom">Nom complet</label>
                <input
                  id="ins-nom" type="text" autoComplete="name" placeholder="Prénom Nom"
                  value={nom} onChange={(e) => setNom(e.target.value)} aria-invalid={invalide('nom')} required
                />
              </div>
              <div className="champ">
                <label htmlFor="ins-email">Adresse e-mail</label>
                <input
                  id="ins-email" type="email" autoComplete="email" placeholder="vous@swiftapp.ml"
                  value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={invalide('email')} required
                />
              </div>
              <div className="champ">
                <label htmlFor="ins-motDePasse">Mot de passe</label>
                <input
                  id="ins-motDePasse" type="password" autoComplete="new-password" placeholder="8 caractères minimum"
                  minLength={8} value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)}
                  aria-invalid={invalide('motDePasse')} required
                />
              </div>
              <div className="champ">
                <label htmlFor="ins-confirmation">Confirmer le mot de passe</label>
                <input
                  id="ins-confirmation" type="password" autoComplete="new-password" placeholder="Retapez le mot de passe"
                  value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
                  aria-invalid={invalide('confirmation')} required
                />
              </div>

              <p className="note-acces">{NOTE_COMPTE}</p>
              <p className="erreur" role="alert" aria-live="assertive">{message}</p>

              <button className="btn btn-plein" type="submit" disabled={enCours !== null}>
                {enCours === 'inscription' ? 'Création…' : 'Créer mon compte'}
              </button>
            </>
          )}

          {contenu === 'inscription' && compteCree !== null && (
            <>
              <p className="compte-cree" role="status">{messageCompteCree(compteCree)}</p>
              <p className="note-acces">{NOTE_COMPTE}</p>
              <button className="btn btn-plein" type="button" onClick={() => changerMode('connexion')}>
                Aller à la connexion
              </button>
            </>
          )}
          </form>
          )}
        </div>
        </>
        )}
      </div>
    </main>
  )
}
